import React, { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import useAnimatedCounter from './useAnimatedCounter';

/**
 * Returns the score tier config based on score value.
 */
function getScoreTier(score) {
  if (score >= 85) {
    return {
      color: 'var(--sr-score-greenlit)',
      label: '1895 GREENLIT',
      tier: 'greenlit',
    };
  }
  if (score >= 70) {
    return {
      color: 'var(--sr-score-high)',
      label: 'Promising',
      tier: 'high',
    };
  }
  if (score >= 50) {
    return {
      color: 'var(--sr-score-mid)',
      label: 'Develop Further',
      tier: 'mid',
    };
  }
  return {
    color: 'var(--sr-score-low)',
    label: 'Pass',
    tier: 'low',
  };
}

/**
 * SRScoreRing — Animated SVG score ring with counting number.
 *
 * @param {Object}  props
 * @param {number}  props.score       - Score value 0-100
 * @param {number}  [props.size=200]  - SVG size in px
 * @param {number}  [props.strokeWidth=8] - Ring stroke width
 * @param {number}  [props.delay=0]   - Animation delay in ms
 * @param {boolean} [props.showBadge=true] - Show tier badge below score
 */
const SRScoreRing = memo(function SRScoreRing({
  score,
  size = 200,
  strokeWidth = 8,
  delay = 0,
  showBadge = true,
}) {
  const displayValue = useAnimatedCounter(score, 2000, delay);
  const tier = useMemo(() => getScoreTier(score), [score]);

  const center = size / 2;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = score / 100;
  const isGreenlit = score >= 85;

  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          position: 'relative',
          width: size,
          height: size,
        }}
        className={isGreenlit ? 'sr-gold-glow' : undefined}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Background track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--sr-border-subtle)"
            strokeWidth={strokeWidth}
          />

          {/* Animated progress ring */}
          <motion.circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={tier.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: circumference * (1 - progress) }}
            transition={{
              duration: 2,
              delay: delay / 1000,
              ease: [0.22, 1, 0.36, 1],
            }}
          />
        </svg>

        {/* Center score number */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--sr-font-display)',
              fontSize: size * 0.26,
              fontWeight: 700,
              color: tier.color,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {displayValue}
          </span>
          <span
            style={{
              fontFamily: 'var(--sr-font-mono)',
              fontSize: size * 0.065,
              color: 'var(--sr-text-tertiary)',
              marginTop: 4,
              letterSpacing: '0.05em',
            }}
          >
            / 100
          </span>
        </div>
      </div>

      {/* Badge label */}
      {showBadge && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: (delay + 1200) / 1000 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isGreenlit ? '6px 16px' : '4px 12px',
            borderRadius: 100,
            fontFamily: 'var(--sr-font-body)',
            fontSize: isGreenlit ? 'var(--sr-text-caption)' : 'var(--sr-text-micro)',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: isGreenlit ? 'var(--sr-gold-bright)' : tier.color,
            background: isGreenlit
              ? 'var(--sr-gold-glow)'
              : `${tier.color}15`,
            border: isGreenlit
              ? '1px solid rgba(201, 168, 76, 0.35)'
              : `1px solid ${tier.color}25`,
          }}
          className={isGreenlit ? 'sr-gold-glow' : undefined}
        >
          {tier.label}
        </motion.div>
      )}
    </div>
  );
});

export default SRScoreRing;
